import { faClose, faMotorcycle } from "@fortawesome/free-solid-svg-icons"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../components/Button/Button"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import { ECourierStatusType } from "../../Enum/ECourierStatusType"
import { CourierEntity } from "../../Objects/Courier/CourierEntity"
import defaultStyle from "../../styles/defaultPageStyle.module.css"

interface AssignCourierPopupProps {
  isOpen: boolean
  couriers: CourierEntity[]
  selectedCourierId: string
  allowRandomCourier: boolean
  onClose: () => void
  onSelectCourier: (courierId: string) => void
  onConfirmSelected: () => void
  onConfirmRandom: () => void
}

export const AssignCourierPopup = ({
  isOpen,
  couriers,
  selectedCourierId,
  allowRandomCourier,
  onClose,
  onSelectCourier,
  onConfirmSelected,
  onConfirmRandom,
}: AssignCourierPopupProps) => {
  const { t } = useTranslation("fulfillment")
  const hasOnlineCouriers = couriers.some((courier) => courier.status === ECourierStatusType.Online)
  const sortedCouriers = useMemo(
    () => [...couriers].sort((a, b) => Number(b.status === ECourierStatusType.Online) - Number(a.status === ECourierStatusType.Online)),
    [couriers],
  )

  if (!isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("courier_popup_title")}
              icon={faMotorcycle}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"

                  descriptionKey="interactionDescriptions:pages.fulfillmentPage.assignCourierPopup.button.onClose" />
              }
            />

            <div className="mb-3 mt-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-left text-xs text-slate-700">
              <p className="font-bold text-red-700">{t("courier_responsibility_title")}</p>
              <p className="mt-1">{t("courier_responsibility")}</p>
              {!allowRandomCourier && <p className="mt-2 font-bold text-orange-700">{t("free_shipping_restriction")}</p>}
            </div>

            <div className="flex max-h-[52dvh] flex-col gap-1 overflow-y-auto p-1 pt-2 text-center">
              {couriers.length === 0 && <p className="mt-5 text-md font-bold text-gray-500">{t("no_couriers")}</p>}
              {sortedCouriers.map((courier) => {
                const offline = courier.status !== ECourierStatusType.Online
                const selected = selectedCourierId === courier.id

                return (
                  <label
                    key={courier.id}
                    className={`flex items-center justify-between rounded-lg border-2 p-2 transition ${
                      offline ? "cursor-not-allowed bg-slate-50 opacity-70" : "cursor-pointer bg-white hover:border-orange-500"
                    } ${selected ? "border-orange-400 ring-2 ring-orange-500/20" : "border-slate-200"}`}
                  >
                    <span className="text-left">
                      <strong className="block text-slate-900">{courier.personEntity.name}</strong>
                      <small className={offline ? "text-red-700" : "text-emerald-700"}>
                        {offline ? t("offline") : t("online")}
                      </small>
                    </span>
                    <input
                      type="radio"
                      checked={selected}
                      onChange={() => onSelectCourier(courier.id)}
                      disabled={offline}

                  data-interaction-description-key="interactionDescriptions:pages.fulfillmentPage.assignCourierPopup.input.input31" />
                  </label>
                )
              })}
            </div>

            <div className="mt-4 flex justify-center gap-2">
              {couriers.length > 0 && (
                <Button
                  type="primary"
                  size="medium"
                  label={t("confirm_selected")}
                  disabled={!selectedCourierId || !hasOnlineCouriers}
                  onClick={onConfirmSelected}

                  descriptionKey="interactionDescriptions:pages.fulfillmentPage.assignCourierPopup.button.tConfirmSelected" />
              )}
              {allowRandomCourier && (
                <Button type="secondary" size="medium" label={t("search_courier")} onClick={onConfirmRandom} descriptionKey="interactionDescriptions:pages.fulfillmentPage.assignCourierPopup.button.tSearchCourier" />
              )}
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
